import { OktaAuth } from '@okta/okta-auth-js';
import { UserClaims, CustomUserClaims } from '@okta/okta-auth-js';
import { createContext, ReactNode, useContext, useState } from 'react';

// Module-level auth reference for non-React access (like API interceptors)
let currentOktaAuth: OktaAuth | null = null;

export const getCurrentAuthToken = (): string | null => {
  if (!currentOktaAuth) return null;

  try {
    const token = currentOktaAuth.getAccessToken();
    return token && typeof token === 'string' && token.length > 0 ? token : null;
  } catch (error) {
    // Log for debugging in development
    if (process.env.NODE_ENV === 'development') {
      console.warn('Failed to get access token:', error);
    }
    return null;
  }
};

const setCurrentOktaAuth = (auth: OktaAuth | null) => {
  currentOktaAuth = auth;
};

export interface AuthContextProps {
  user: UserClaims<CustomUserClaims> | null;
  oktaAuth: OktaAuth | null;
  isAuthenticated: boolean;
  isTokenReady: boolean;
  isLoading: boolean;
  updateUser: (user: UserClaims<CustomUserClaims> | null) => void;
  updateOktaAuth: (auth: OktaAuth) => void;
}

export interface AuthContextProviderProps {
  children: ReactNode;
}

export const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const AuthContextProvider = ({ children }: AuthContextProviderProps) => {
  const [user, setUser] = useState<UserClaims<CustomUserClaims> | null>(null);
  const [oktaAuth, setOktaAuth] = useState<OktaAuth | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const updateUser = (newUser: UserClaims<CustomUserClaims> | null) => {
    setUser(newUser);
    setIsLoading(false); // User fetch complete
  };

  const updateOktaAuth = (auth: OktaAuth) => {
    setCurrentOktaAuth(auth);
    setOktaAuth(auth);
  };

  const isAuthenticated = !!user && !!oktaAuth;

  const isTokenReady = isAuthenticated && !!getCurrentAuthToken();

  const contextValue: AuthContextProps = {
    user,
    oktaAuth,
    isAuthenticated,
    isTokenReady,
    isLoading,
    updateUser,
    updateOktaAuth,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

// Custom hook to easily access the AuthContext
export const useAuth = (): AuthContextProps => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthContextProvider');
  }
  return context;
};
